import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { closeViewAssignmentsModal, viewAssignments, clearAssignments } from '../tutorAssignmentSlice/tutorAssignmentSlice';
import './ViewAssignmentsModal.scss';
import { getUserData } from 'Services/API';
import Loading from '../../../components/Reports/blocks/Loading';
import CloseIcon from "react-icons/lib/md/close";
import moment from 'moment';

export default function ViewAssignmentsModal() {
  const dispatch = useDispatch();
  const isOpen = useSelector(state => state.tutorAssignment.isViewAssignmentsModalOpen);
  const isModalOpen = useSelector(state => state.tutorAssignment.isModalOpen);
  const stories = useSelector(state => state.tutorAssignment.storyBooks);
  const roster = useSelector(state => state.reports.roster);
  const studentIds = roster?.data?.student?.map(student => student.id);
  const isRosterLoaded = roster?.data?.student;
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const assignments = useSelector(state => state.tutorAssignment.assignments) || [];
  const { districtId, schoolId } = getUserData();

  useEffect(() => {
    if (districtId && schoolId && studentIds?.length > 0) {
      setReadyToFetch(true);
    } else {
      setReadyToFetch(false);
    }
  }, [districtId, schoolId, studentIds]);

  useEffect(() => {
    if ((isOpen || isModalOpen) && readyToFetch && assignments.length === 0 && roster.data && roster.status !== 'PENDING') {
      dispatch(viewAssignments({ studentIds, districtId, schoolId }))
        .unwrap()
        .then(() => setIsDataLoaded(true))
        .catch((error) => {
          console.error('Error:', error);
          setIsDataLoaded(false);
        });
    }
  }, [roster.data, assignments, isOpen, isModalOpen, readyToFetch, studentIds, roster.status]);

  useEffect(() => {
    dispatch(clearAssignments());
    setIsDataLoaded(false);
  }, [roster.data])

  const handleCloseModal = () => {
    dispatch(closeViewAssignmentsModal());
    dispatch(clearAssignments());
    setIsDataLoaded(false);
  };

  const getStudentName = (studentId) => {
    if (!isRosterLoaded) return <Loading />;
    const student = roster?.data?.student?.find(s => s.id === studentId);
    return student ? `${student.last_name}, ${student.first_name}` : '';
  };

  const groupedAssignments = isDataLoaded && Array.isArray(assignments) ? roster?.data?.student?.map(student => {
    const studentAssignment = assignments.find(studentAssignment =>
      studentAssignment?.assignments?.some(assignment => assignment.entityId === student.id)
    );

    if (studentAssignment?.assignments) {
      const assignment = studentAssignment.assignments[0];
      return {
        storyId: assignment.manifest[0]?.storyId,
        createdAt: assignment.createdAt,
        students: [student.id],
        key: `assigned-${student.id}`,
        status: assignment.status
      };
    } else {
      return {
        storyId: null,
        createdAt: null,
        students: [student.id],
        key: `unassigned-${student.id}`,
        status: null
      };
    }
  }) : [];

  const sortedAssignments = groupedAssignments;

  if (!isOpen) return null;

  return (
    <div className="view-tutor-assignments-modal">
      <div className="view-tutor-assignments-modal__content">
        <CloseIcon
          className="view-tutor-assignments-modal__close-button"
          onClick={handleCloseModal}
        />
        <h2 className='view-tutor-assignments-modal-header'>Current Assignments</h2>

        {!isDataLoaded ? (
          <Loading />
        ) : (
          <div className="view-tutor-assignments-table-wrapper">
            <AssignmentTable
              groupedAssignments={sortedAssignments}
              stories={stories}
              getStudentName={getStudentName}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const AssignmentTable = ({ groupedAssignments, stories, getStudentName }) => {
  const assignmentStatus = {
    'ASSIGNED': 'Assigned',
    'IN_PROGRESS': 'In Progress',
    'COMPLETE': 'Complete',
    'DONE': 'Done'
  }

  return (
    <table className="tutor-assignments-table">
      <thead className="tutor-assignments-table__header">
        <tr className="tutor-assignments-table__header-row">
          <th className="tutor-assignments-table__header-cell">Student List</th>
          <th className="tutor-assignments-table__header-cell">Story</th>
          <th className="tutor-assignments-table__header-cell">Creation Date</th>
          <th className="tutor-assignments-table__header-cell">Status</th>
        </tr>
      </thead>
      <tbody className="tutor-assignments-table__body">
        {groupedAssignments.map(group => {
          const story = stories[group.storyId];
          const studentNames = group.students
            .map(studentId => getStudentName(studentId))
            .join(', ');
          const createdAt = group.createdAt ? moment.utc(group.createdAt).local().format('M/D/YY') : '';
          const status = group.status ? assignmentStatus[group.status] : '';
          return (
            <tr
              key={group.key || `${group.storyId}-${group.createdAt}`}
              className="tutor-assignments-table__row"
            >
              <td className="tutor-assignments-table__cell">{studentNames}</td>
              <td className="tutor-assignments-table__cell">{story?.title || ''}</td>
              <td className="tutor-assignments-table__cell">
                {createdAt}
              </td>
              <td className="tutor-assignments-table__cell">
                {status}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};